// Dashboard-related type definitions
import { ComponentType, ReactNode } from 'react';
import { UserRole } from './enums/UserRole';
import { BackgroundDisplayMode } from './BackgroundDisplayMode';
import { WeekInfo } from '@/utils/week';

/**
 * Dashboard tab identifiers enum
 * Use this instead of string literals for type safety
 */
export enum DashboardTab {
  DASHBOARD = 'dashboard',
  MY_BOARD = 'my-board',
  PEOPLE = 'people',
  MANAGE_PEOPLE = 'manage-people',
  ORGS_MANAGEMENT = 'orgs-management',
  CONFIG_MANAGEMENT = 'config-management',
  BOARD_TEMPLATES = 'board-templates',
  LEARNING_PROGRESS = 'learning_progress',
  SCHEDULED_JOBS = 'scheduled-jobs',
  JOB_RUN_DETAIL = 'job-run-detail',
  SYSTEM_HEALTH = 'system-health',
  GROUPS = 'groups',
  COHORTS = 'cohorts',
}

/**
 * Content layout presets for screen width constraints.
 * Each screen declares its preferred layout — AppLayout applies the constraint.
 *
 * - FULL: No max-width, uses entire available space (e.g. kanban board)
 * - NARROW: 600px centered — focused content like profiles, velocity
 * - MEDIUM: 896px centered — admin forms, config screens
 * - WIDE: 1200px centered — dashboards with multiple columns
 */
export enum ContentLayout {
  FULL = 'full',
  NARROW = 'narrow',
  MEDIUM = 'medium',
  WIDE = 'wide',
}

/**
 * Scroll mode for each tab.
 * - PAGE: natural body scroll (header stays sticky, page grows with content)
 * - CONTAINED: viewport-locked (h-screen, overflow-hidden) — required for Kanban and split-panel screens
 */
export enum TabScrollMode {
  PAGE = 'page',
  CONTAINED = 'contained',
}

export const TAB_SCROLL_MODE: Record<DashboardTab, TabScrollMode> = {
  [DashboardTab.DASHBOARD]: TabScrollMode.PAGE,
  [DashboardTab.MY_BOARD]: TabScrollMode.CONTAINED,
  [DashboardTab.PEOPLE]: TabScrollMode.PAGE,
  [DashboardTab.MANAGE_PEOPLE]: TabScrollMode.PAGE,
  [DashboardTab.ORGS_MANAGEMENT]: TabScrollMode.PAGE,
  [DashboardTab.CONFIG_MANAGEMENT]: TabScrollMode.PAGE,
  [DashboardTab.BOARD_TEMPLATES]: TabScrollMode.PAGE,
  [DashboardTab.LEARNING_PROGRESS]: TabScrollMode.PAGE,
  [DashboardTab.SCHEDULED_JOBS]: TabScrollMode.PAGE,
  [DashboardTab.JOB_RUN_DETAIL]: TabScrollMode.PAGE,
  [DashboardTab.SYSTEM_HEALTH]: TabScrollMode.PAGE,
  [DashboardTab.GROUPS]: TabScrollMode.CONTAINED,
  [DashboardTab.COHORTS]: TabScrollMode.CONTAINED,
};

/**
 * Maps every DashboardTab to its content layout preset.
 * TypeScript enforces exhaustiveness — adding a new tab without a layout is a compile error.
 */
export const TAB_CONTENT_LAYOUT: Record<DashboardTab, ContentLayout> = {
  [DashboardTab.DASHBOARD]: ContentLayout.WIDE,
  [DashboardTab.MY_BOARD]: ContentLayout.FULL,
  [DashboardTab.PEOPLE]: ContentLayout.FULL,
  [DashboardTab.MANAGE_PEOPLE]: ContentLayout.FULL,
  [DashboardTab.ORGS_MANAGEMENT]: ContentLayout.FULL,
  [DashboardTab.CONFIG_MANAGEMENT]: ContentLayout.MEDIUM,
  [DashboardTab.BOARD_TEMPLATES]: ContentLayout.FULL,
  [DashboardTab.LEARNING_PROGRESS]: ContentLayout.MEDIUM,
  [DashboardTab.SCHEDULED_JOBS]: ContentLayout.MEDIUM,
  [DashboardTab.JOB_RUN_DETAIL]: ContentLayout.MEDIUM,
  [DashboardTab.SYSTEM_HEALTH]: ContentLayout.WIDE,
  [DashboardTab.GROUPS]: ContentLayout.FULL,
  [DashboardTab.COHORTS]: ContentLayout.FULL,
};

export interface TabItem {
  id: string;
  name: string;
  icon?: string;
  component?: ComponentType;
}

export interface NavigationItem {
  id: string;
  label: string;
  path?: string;
  icon?: string;
}

export interface UserMood {
  id: string;
  label: string;
  emoji: string;
}

export interface BreadcrumbItem {
  label: string;
  path?: string;
  isActive?: boolean;
  onClick?: () => void;
}

export interface ProfileSettings {
  displayName: string;
  profilePicture?: string;
  mood: UserMood;
  theme: 'light' | 'dark' | 'auto';
}

export interface SidebarState {
  isCollapsed: boolean;
  activeTab: string;
  tabs: TabItem[];
}

export interface HeaderProps {
  onSidebarToggle: () => void;
  isSidebarCollapsed: boolean;
  breadcrumbs: BreadcrumbItem[];
  onProfileClick: () => void;
  onLogoClick?: () => void;
  currentBoardId?: string;
  currentBoardName?: string;
  onBoardChange?: (boardId: string | undefined) => void;
  // Week filtering props
  showWeekNavigator?: boolean;
  selectedWeek?: WeekInfo;
  onWeekChange?: (week: WeekInfo) => void;
  // Board settings props
  showBoardSettings?: boolean;
  onBoardSettingsClick?: () => void;
  // Screen-specific header actions (e.g. view toggle, sync button)
  headerExtras?: ReactNode;
  boardBackground?: {
    absolute_url: string;
    display_mode?: BackgroundDisplayMode;
    dimensions?: {
      width: number;
      height: number;
    };
  } | null;
}

export interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  activeTab: string;
  onTabChange: (tabId: string) => void;
  tabs: TabItem[];
}

export interface ProfilePopupProps {
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void;
}

export interface DashboardLayoutProps {
  children: ReactNode;
}

// Mood options for user selection — big child-friendly superset
export const MOOD_OPTIONS: UserMood[] = [
  // Positive / energetic
  { id: 'happy', label: 'Happy', emoji: '😊' },
  { id: 'excited', label: 'Excited', emoji: '🚀' },
  { id: 'joyful', label: 'Joyful', emoji: '😄' },
  { id: 'silly', label: 'Silly', emoji: '🤪' },
  { id: 'laughing', label: 'Laughing', emoji: '😂' },
  { id: 'proud', label: 'Proud', emoji: '🦁' },
  { id: 'loved', label: 'Loved', emoji: '🥰' },
  { id: 'cool', label: 'Cool', emoji: '😎' },
  { id: 'starry', label: 'Starry', emoji: '🤩' },
  { id: 'dancing', label: 'Dancing', emoji: '💃' },
  { id: 'celebrating', label: 'Party', emoji: '🥳' },
  { id: 'sparkle', label: 'Sparkle', emoji: '✨' },

  // Focused / learning
  { id: 'focused', label: 'Focused', emoji: '🎯' },
  { id: 'curious', label: 'Curious', emoji: '🤔' },
  { id: 'nerdy', label: 'Nerdy', emoji: '🤓' },
  { id: 'reading', label: 'Reading', emoji: '📖' },
  { id: 'thinking', label: 'Thinking', emoji: '💭' },
  { id: 'lightbulb', label: 'Eureka', emoji: '💡' },
  { id: 'detective', label: 'Detective', emoji: '🔍' },
  { id: 'brainy', label: 'Brainy', emoji: '🧠' },

  // Calm / chill
  { id: 'calm', label: 'Calm', emoji: '🧘' },
  { id: 'peaceful', label: 'Peaceful', emoji: '☮️' },
  { id: 'sleepy', label: 'Sleepy', emoji: '😴' },
  { id: 'dreamy', label: 'Dreamy', emoji: '🌙' },
  { id: 'cozy', label: 'Cozy', emoji: '🧸' },
  { id: 'relaxed', label: 'Relaxed', emoji: '😌' },
  { id: 'cloudy', label: 'Cloudy', emoji: '☁️' },

  // Creative / playful
  { id: 'creative', label: 'Creative', emoji: '🎨' },
  { id: 'musical', label: 'Musical', emoji: '🎵' },
  { id: 'magical', label: 'Magical', emoji: '🪄' },
  { id: 'superhero', label: 'Superhero', emoji: '🦸' },
  { id: 'pirate', label: 'Pirate', emoji: '🏴‍☠️' },
  { id: 'astronaut', label: 'Astronaut', emoji: '🧑‍🚀' },
  { id: 'fairy', label: 'Fairy', emoji: '🧚' },
  { id: 'wizard', label: 'Wizard', emoji: '🧙' },
  { id: 'robot', label: 'Robot', emoji: '🤖' },
  { id: 'alien', label: 'Alien', emoji: '👽' },

  // Nature / animal vibes
  { id: 'butterfly', label: 'Butterfly', emoji: '🦋' },
  { id: 'sunshine', label: 'Sunshine', emoji: '☀️' },
  { id: 'rainbow', label: 'Rainbow', emoji: '🌈' },
  { id: 'flower', label: 'Flower', emoji: '🌸' },
  { id: 'puppy', label: 'Puppy', emoji: '🐶' },
  { id: 'kitty', label: 'Kitty', emoji: '🐱' },
  { id: 'bunny', label: 'Bunny', emoji: '🐰' },
  { id: 'panda', label: 'Panda', emoji: '🐼' },
  { id: 'unicorn', label: 'Unicorn', emoji: '🦄' },
  { id: 'dolphin', label: 'Dolphin', emoji: '🐬' },
  { id: 'dino', label: 'Dino', emoji: '🦕' },
  { id: 'dragon', label: 'Dragon', emoji: '🐉' },

  // Hungry / food moods
  { id: 'hungry', label: 'Hungry', emoji: '🍕' },
  { id: 'snacky', label: 'Snacky', emoji: '🍪' },
  { id: 'sweet', label: 'Sweet', emoji: '🍩' },
  { id: 'icecream', label: 'Ice Cream', emoji: '🍦' },

  // Feelings / emotional
  { id: 'shy', label: 'Shy', emoji: '🙈' },
  { id: 'brave', label: 'Brave', emoji: '💪' },
  { id: 'grateful', label: 'Grateful', emoji: '🙏' },
  { id: 'mindblown', label: 'Mind Blown', emoji: '🤯' },
  { id: 'mischievous', label: 'Sneaky', emoji: '😏' },
  { id: 'nervous', label: 'Nervous', emoji: '😬' },
  { id: 'grumpy', label: 'Grumpy', emoji: '😤' },
  { id: 'sad', label: 'Sad', emoji: '😢' },
  { id: 'scared', label: 'Scared', emoji: '😱' },
  { id: 'confused', label: 'Confused', emoji: '😵‍💫' },
  { id: 'sick', label: 'Sick', emoji: '🤒' },

  // Sporty / active
  { id: 'sporty', label: 'Sporty', emoji: '⚽' },
  { id: 'strong', label: 'Strong', emoji: '🏋️' },
  { id: 'speedy', label: 'Speedy', emoji: '⚡' },
  { id: 'champion', label: 'Champion', emoji: '🏆' },

  // Weather / cosmic
  { id: 'stormy', label: 'Stormy', emoji: '⛈️' },
  { id: 'fire', label: 'On Fire', emoji: '🔥' },
  { id: 'snowflake', label: 'Frosty', emoji: '❄️' },
  { id: 'galaxy', label: 'Galaxy', emoji: '🌌' },
  { id: 'heart', label: 'Lovey', emoji: '❤️' },
];

// Default tabs configuration — order here determines sidebar order.
// User-facing tabs first, then admin tabs, then superadmin-only tabs.
export const DEFAULT_TABS: TabItem[] = [
  // ── User-facing ──────────────────────────────────────────────────────────
  {
    id: DashboardTab.MY_BOARD,
    name: 'Board',
    icon: 'LayoutDashboard',
  },
  {
    id: DashboardTab.PEOPLE,
    name: 'People',
    icon: 'UserSearch',
  },
  {
    id: DashboardTab.LEARNING_PROGRESS,
    name: 'Learning Progress',
    icon: 'Activity',
  },
  {
    id: DashboardTab.GROUPS,
    name: 'Groups',
    icon: 'Users',
  },
  {
    id: DashboardTab.COHORTS,
    name: 'Cohorts', // MENTOR + ADMIN
    icon: 'Users',
  },
  // ── Admin ─────────────────────────────────────────────────────────────────
  {
    id: DashboardTab.MANAGE_PEOPLE,
    name: 'Manage People',
    icon: 'Users',
  },
  {
    id: DashboardTab.BOARD_TEMPLATES,
    name: 'Board Templates',
    icon: 'LayoutTemplate',
  },
  {
    id: DashboardTab.CONFIG_MANAGEMENT,
    name: 'Config',
    icon: 'Settings',
  },
  {
    id: DashboardTab.SCHEDULED_JOBS,
    name: 'Jobs',
    icon: 'Zap',
  },
  // ── Superadmin (own org only) ─────────────────────────────────────────────
  {
    id: DashboardTab.ORGS_MANAGEMENT,
    name: 'Organizations',
    icon: 'Building2',
  },
  {
    id: DashboardTab.SYSTEM_HEALTH,
    name: 'System Health',
    icon: 'Monitor',
  },
];

/**
 * Role-based default tab configuration
 * Maps each user role to their default landing tab
 *
 * - ADMIN: Board (task management)
 * - MENTOR: Board (task management)
 * - STUDENT: Board (learning tasks)
 */
export const ROLE_DEFAULT_TAB: Record<UserRole, DashboardTab> = {
  [UserRole.ADMIN]: DashboardTab.MY_BOARD,
  [UserRole.MENTOR]: DashboardTab.MY_BOARD,
  [UserRole.STUDENT]: DashboardTab.MY_BOARD,
};

/**
 * Get the default tab for a given user role
 * @param role - User role from UserRole enum
 * @returns The default dashboard tab for that role
 */
export function getDefaultTabForRole(role: UserRole): DashboardTab {
  return ROLE_DEFAULT_TAB[role] ?? DashboardTab.MY_BOARD;
}
